import { getTargetedFilter } from '@databases/filters/utils/getNestedFilter';
import { FieldType } from '@databases/model/field';
import {
  CollabFilterType,
  MutableCollabFilterBooleanExpression,
} from '@databases/model/views/view-filter';
import { DatabaseFilterType } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { v4 as uuidv4 } from 'uuid';

import {
  calculationToCollabMapping,
  CardTypeToCollabMapping,
  CardWidthToCollabMapping,
  coverFieldIdToCollabMapping,
  filterToCollabJSON,
  filterUsageTypeToCollabMapping,
} from '../../../collab/convert-to-collab';
import { YJSChangeHandler } from '../../types';
import { applyDeltaValue } from '../../utils/apply-delta-value';

export const bulkApplySavedViewChanges: YJSChangeHandler<
  DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES
> = ({ ydb, change }) => {
  ydb.mutate((db) => {
    change.changes.forEach((viewChange) => {
      switch (viewChange.type) {
        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.layoutType = CardTypeToCollabMapping[viewChange.newLayoutType];
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.cardWidth = CardWidthToCollabMapping[viewChange.newCardWidth];
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.coverField = coverFieldIdToCollabMapping(viewChange.newCoverFieldId);
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.groupFieldId = viewChange.newGroupFieldId;
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.dateFieldId = viewChange.newDateFieldId;
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_NAME: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.applyNameDelta(viewChange.delta);
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.setFieldVisibility(
              viewChange.fieldKey,
              viewChange.fieldValue,
              db.fields
            );
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sort = viewChange.sort;
          }

          break;
        }

        case DatabaseChangeType.UPDATE_SAVED_VIEW_SORT_VALUE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.updateSortForFieldId(viewChange.fieldId, viewChange.ascending);
          }

          break;
        }

        case DatabaseChangeType.INSERT_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.insertSort(viewChange.index, viewChange.sort);
          }

          break;
        }

        case DatabaseChangeType.APPEND_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.appendSort(viewChange.sort);
          }

          break;
        }

        case DatabaseChangeType.DELETE_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.deleteSortForFieldId(viewChange.fieldId);
          }

          break;
        }

        case DatabaseChangeType.REORDER_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.reorderSortForFieldId(viewChange.fieldId, viewChange.newIndex);
          }

          break;
        }

        case DatabaseChangeType.CLEAR_SAVED_VIEW_SORT: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            view.sorts.clearAll();
          }

          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW_FILTER_USAGE_TYPE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            if (
              view.filter &&
              (view.filter.type === CollabFilterType.AND ||
                view.filter.type === CollabFilterType.OR)
            ) {
              view.filter.type = filterUsageTypeToCollabMapping(
                viewChange.newFilterUsageType
              );
            }
          }

          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW_FILTER_VALUE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            if (!view.filter || viewChange.path === undefined) {
              view.setFilter({
                id: uuidv4(),
                type: CollabFilterType.AND,
                value: [],
              });
            } else {
              const [currentFilter, pathDepth] = getTargetedFilter(
                view.filter,
                viewChange.path
              );

              if (
                currentFilter.type === CollabFilterType.AND ||
                currentFilter.type === CollabFilterType.OR
              ) {
                const targetFilter = currentFilter.value[viewChange.path[pathDepth]];

                if (
                  viewChange.change &&
                  targetFilter &&
                  (targetFilter.type === CollabFilterType.EQUALS ||
                    targetFilter.type === CollabFilterType.GREATER ||
                    targetFilter.type === CollabFilterType.LESS)
                ) {
                  applyDeltaValue(
                    (type) => targetFilter.setValue(type),
                    viewChange.change
                  );
                } else if (
                  targetFilter &&
                  targetFilter.type === CollabFilterType.CONTAINS &&
                  viewChange.newFilter.type !== DatabaseFilterType.EMPTY
                ) {
                  if (typeof viewChange.newFilter.value === 'string') {
                    targetFilter.value = viewChange.newFilter.value;
                  }
                }
              }
            }
          }

          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW_FILTER:
        case DatabaseChangeType.SET_SAVED_VIEW_FILTER_FIELD_ID:
        case DatabaseChangeType.SET_SAVED_VIEW_FILTER_TYPE: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            if (!viewChange.newFilter) {
              view.clearFilter();
            } else if (view.filter && viewChange.path) {
              const [currentFilter, pathDepth] = getTargetedFilter(
                view.filter,
                viewChange.path
              );

              if (
                currentFilter.type === CollabFilterType.AND ||
                currentFilter.type === CollabFilterType.OR
              ) {
                const collabFilter = filterToCollabJSON(viewChange.newFilter, {});

                if (collabFilter) {
                  currentFilter.setFilter(viewChange.path[pathDepth], collabFilter);
                } else {
                  currentFilter.remove(viewChange.path[pathDepth]);

                  if (currentFilter.value.length === 0 && pathDepth === 0) {
                    view.clearFilter();
                  }
                }
              }
            }
          }
          break;
        }

        case DatabaseChangeType.APPEND_SAVED_VIEW_FILTER: {
          const view = db.viewsById[viewChange.viewId];

          if (view) {
            if (!view.filter) {
              const filter = view.setFilter({
                id: uuidv4(),
                type: CollabFilterType.AND,
                value: [],
              }) as MutableCollabFilterBooleanExpression;

              if (viewChange.newFilter.type === DatabaseFilterType.EQUALS) {
                filter.add({
                  id: filter.id,
                  type: CollabFilterType.EQUALS,
                  fieldId: '',
                  value: {
                    type: FieldType.Text,
                    value: viewChange.newFilter.value.value as string,
                  },
                });
              }
            } else {
              const currentFilter = viewChange.path
                ? getTargetedFilter(view.filter, viewChange.path)[0]
                : view.filter;

              if (
                currentFilter.type === CollabFilterType.AND ||
                currentFilter.type === CollabFilterType.OR
              ) {
                const collabFilter = filterToCollabJSON(viewChange.newFilter, {});
                if (collabFilter) {
                  currentFilter.add(collabFilter);
                }
              }
            }
          }
          break;
        }

        case DatabaseChangeType.REMOVE_SAVED_VIEW_FILTER: {
          const view = db.viewsById[viewChange.viewId];
          if (view) {
            if (!view.filter || !viewChange.path) {
              view.clearFilter();
            } else {
              const [currentFilter, pathDepth] = getTargetedFilter(
                view.filter,
                viewChange.path
              );

              if (
                currentFilter.type === CollabFilterType.AND ||
                currentFilter.type === CollabFilterType.OR
              ) {
                currentFilter.remove(viewChange.path[pathDepth]);

                if (currentFilter.value.length === 0 && pathDepth === 0) {
                  view.clearFilter();
                }
              }
            }
          }

          break;
        }

        case DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.setCalculation(
                viewChange.fieldId,
                calculationToCollabMapping[viewChange.value]
              );
            }
          });
          break;

        case DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.removeCalculation(viewChange.fieldId);
            }
          });
          break;

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.showNumberedEntries = viewChange.newShowNumberedEntries;
            }
          });
          break;

        case DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_WRAP_ALL_TEXT:
          ydb.mutate((db) => {
            const view = db.viewsById[viewChange.viewId];

            if (view) {
              view.wrapAllText = viewChange.newWrapAllText;
            }
          });
          break;
      }
    });
  });
};
